<template>
  <div class="u-center mb-2.5">
    <div
      ref="stickyMastheadRef"
      class="flex justify-center z-30"
      style="width: 100%"
      :class="{ 'sticky-masthead': isSticky, 'move-top': transitionSticky }"
    >
      <div class="sticky-inner-area" :class="{ collapse: isSticky && isMobile }">
        <slot />
      </div>
      <div v-show="isMobile && isSticky" class="u-center absolute right-0 top-0 bottom-0 z-1" :style="{ width: buttonDivWidth + 'px' }">
        <transition name="fade">
          <OButton v-if="!loading && isSticky && isMobile" pure @click="removeSticky()">
            <OIcon icon="chevron-left" svg-class="bg-[#d9d9d9] text-primary-300 h-5 w-5 p-1 rounded rotate-90" />
          </OButton>
        </transition>
      </div>
    </div>
  </div>
</template>
<script>
import { defineComponent, onMounted, ref, computed } from '@nuxtjs/composition-api';
import { useWindowSize } from '@vueuse/core';
import { useDevice } from '~/composables/useDevice';
import { OButton, OIcon } from '~/modules/common/components/ui';
import { onedioEvents } from '~/modules/tracking/scripts/onedioEvents';

export default defineComponent({
  props: ['adSlot'],
  components: { OButton, OIcon },
  setup(props, { emit }) {
    const { sendAdEvent } = onedioEvents();
    const { isMobile } = useDevice();
    const loading = ref(true);
    const isSticky = ref(false);
    const stickyMastheadRef = ref(null);
    const transitionSticky = ref(false);
    const offsetPoint = ref(null);
    let idleTimer;
    let lastScrollTop = 0;
    let headerElement = null;
    let lastScroll = null;

    const adWidth = ref(0);
    const { width } = useWindowSize();

    const buttonDivWidth = computed(() => width.value - adWidth.value - 7);

    function mobileStickyCheck() {
      if (!headerElement || !stickyMastheadRef.value) return;
      const st = window.pageYOffset || document.documentElement.scrollTop;
      if (st > lastScrollTop && lastScroll !== 'down') {
        lastScroll = 'down';
        headerElement.style.transform = 'translateY(-53px)';
        stickyMastheadRef.value.style.transform = 'translateY(-53px)';
      } else if (st < lastScrollTop && lastScroll !== 'up') {
        lastScroll = 'up';
        headerElement.style.transform = 'unset';
        stickyMastheadRef.value.style.transform = 'unset';
      }
      if (st + (isMobile.value === false ? 116 : 53) < offsetPoint.value) {
        removeSticky({ withAnimation: false });
        document.addEventListener('scroll', scrollCheck);
      }
      lastScrollTop = st <= 0 ? 0 : st;
    }
    function scrollCheck() {
      const st = window.pageYOffset || document.documentElement.scrollTop;
      if (st + (isMobile.value === false ? 116 : 53) >= stickyMastheadRef.value.offsetTop) {
        if (!offsetPoint.value) {
          offsetPoint.value = stickyMastheadRef.value.offsetTop;
        }
        clearTimeout(idleTimer);
        isSticky.value = true;
        document.removeEventListener('scroll', scrollCheck);
        if (isMobile.value) {
          document.addEventListener('scroll', mobileStickyCheck);
        } else {
          setTimeout(() => {
            removeSticky();
          }, 3000);
        }
      }
    }
    const removeSticky = ({ withAnimation = true } = {}) => {
      document.removeEventListener('scroll', mobileStickyCheck);
      emit('close-wrapper');
      if (withAnimation) {
        document.removeEventListener('scroll', scrollCheck);
        if (!headerElement || !stickyMastheadRef.value) return;
        stickyMastheadRef.value.style.transform = null;
        headerElement.style.transform = null;
        transitionSticky.value = true;
        stickyMastheadRef.value.addEventListener('transitionend', function () {
          isSticky.value = false;
          transitionSticky.value = false;
        });
      } else {
        isSticky.value = false;
      }
      sendAdEvent('Sticky Ad', 'close_click', 'Masthead');
    };

    onMounted(() => {
      headerElement = document.querySelector('.header__wrapper');
      const googletag = window.googletag;
      window.scrollCheck = removeSticky;

      // Eğer membrana reklamı sticky olursa masthead sticky alanını kaldır
      window.addEventListener('load', () => {
        window.yieldMaster?.addEventListener('VIDEO_AD_STICK', function () {
          removeSticky();
        });
      });

      googletag.cmd.push(function () {
        const slotRenderFunction = (event) => {
          const slot = event.slot;
          if (slot === props.adSlot && !event.isEmpty && !transitionSticky.value && !window.isAdProject) {
            adWidth.value = (event.size[0] <= 1 ? 320 : event.size[0]) || 320;
            loading.value = false;
            if (!isMobile.value) {
              idleTimer = setTimeout(() => {
                document.removeEventListener('scroll', scrollCheck);
              }, 3000);
            }
            if (window.pageYOffset === 0) {
              document.addEventListener('scroll', scrollCheck);
            } else {
              scrollCheck();
              if (isMobile.value) {
                document.addEventListener('scroll', scrollCheck);
              }
            }
            if (isMobile.value) {
              googletag.pubads().removeEventListener('slotRenderEnded', slotRenderFunction);
            }
          }
        };
        googletag.pubads().addEventListener('slotRenderEnded', slotRenderFunction);
      });
    });
    return { isSticky, transitionSticky, stickyMastheadRef, isMobile, removeSticky, loading, buttonDivWidth };
  },
});
</script>
<style lang="scss">
.sticky-masthead {
  position: fixed;
  margin-bottom: 0;
  top: 53px;
  left: 0;
  right: 0;
  background: #fff;
  @include sm() {
    top: 100px;
    height: 276px !important;
    padding: 16px 0 10px;
    background: rgba(240, 240, 240, 0.8);
    backdrop-filter: unset;
  }
}

.move-top {
  transform: translateY(-100px);
  @include sm() {
    transform: translateY(-376px);
  }
}

.sticky-inner-area {
  &.collapse {
    transform: translateX(calc(-1 * ((100vw - 100%) / 2 - 7px)));
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 1s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}
</style>
